import Vue from 'vue'
import Vuex from 'vuex'
import { EXPERTS_MASTER, composeExpert, DEFAULT_EXPERT_STATS } from '../data/experts'
import { EXPERT_CASES } from '../data/cases'
import { TOPICS } from '../data'
import { STORAGE_KEYS, SCORING_CONFIG, ROLES, DEFAULT_ADMINS } from '../data/adminConfig'
import getters from './getters'

Vue.use(Vuex)

const defaultContext = {
  businessStage: '快速扩张期',
  teamStatus: '人心浮动中',
  leadershipStyle: '强势结果导向',
  managementMode: '互联网敏捷模式',
  span: 8,
  levels: 2,
  composition: ['研发', '产品'],
  pressure: 6,
  decisionMode: '民主决策',
  memo: ''
}

const defaultUserStats = {
  points: 1200,
  medals: ['初出茅庐', '战地观察员'],
  experience: '3-5年',
  scale: '5-15人',
  domain: '研发',
  bookmarks: [],
  likes: []
}

function loadFromStorage(key, defaultValue) {
  try {
    const saved = localStorage.getItem(key)
    return saved ? JSON.parse(saved) : defaultValue
  } catch {
    return defaultValue
  }
}

/** 是否允许从 localStorage 读取持久化数据 */
const isPersistenceEnabled = process.env.NODE_ENV !== 'production'

/** 需要持久化的 key 与 mutation 的映射 */
const PERSISTENCE_MAP = {
  [STORAGE_KEYS.CONTEXT]: 'SET_CONTEXT',
  [STORAGE_KEYS.USER_STATS]: 'SET_USER_STATS',
  [STORAGE_KEYS.HISTORY]: 'SET_HISTORY',
  [STORAGE_KEYS.STUDY_RECORDS]: 'SET_STUDY_RECORDS',
  [STORAGE_KEYS.PRACTICE_RECORDS]: 'SET_PRACTICE_RECORDS',
  [STORAGE_KEYS.CONTRIBUTIONS]: 'SET_CONTRIBUTIONS',
  [STORAGE_KEYS.TOPICS]: 'SET_TOPICS',
  [STORAGE_KEYS.CASES]: 'SET_CASES',
  [STORAGE_KEYS.EXPERTS]: 'SET_EXPERTS',
  [STORAGE_KEYS.ADMINS]: 'SET_ADMINS',
  [STORAGE_KEYS.CURRENT_ADMIN_ID]: 'SET_CURRENT_ADMIN_ID',
}

/** Vuex Plugin：初始化时从 localStorage 恢复数据（仅在非生产环境） */
const persistPlugin = (store) => {
  if (!isPersistenceEnabled) {
    console.info('[Store] localStorage persistence disabled in production')
    return
  }

  // 延迟加载：在 store 创建后从 localStorage 恢复
  store.dispatch('loadPersistedState')

  // 跨标签页同步：监听 storage 事件
  const handleStorage = (event) => {
    const mutationType = PERSISTENCE_MAP[event.key]
    if (!mutationType) return

    try {
      const newValue = event.newValue ? JSON.parse(event.newValue) : null
      if (newValue !== null) {
        store.commit(mutationType, newValue)
      }
    } catch (error) {
      console.error('[Store] Cross-tab sync failed for key:', event.key, error)
    }
  }

  window.addEventListener('storage', handleStorage)

  // 清理监听器（虽然 store 通常伴随应用生命周期）
  store._unsubscribeStorage = () => {
    window.removeEventListener('storage', handleStorage)
  }
}

/**
 * 初始化专家运行时数据
 * 将主数据（EXPERTS_MASTER）与派生数据（stats/points/contentCount）组合
 */
function initExperts() {
  return EXPERTS_MASTER.map(master =>
    composeExpert(master, { ...DEFAULT_EXPERT_STATS }, EXPERT_CASES, SCORING_CONFIG)
  )
}

export default new Vuex.Store({
  state: {
    isLoggedIn: false,
    view: 'home',
    isBriefingMode: false,
    context: { ...defaultContext },
    userStats: { ...defaultUserStats },
    history: [],
    studyRecords: [],
    practiceRecords: [],
    experts: initExperts(),
    cases: { ...EXPERT_CASES },
    topics: [...TOPICS],
    scenarios: {},
    prescriptions: {},
    targetTopicId: null,
    pendingQuery: '',
    selectedTopic: null,
    diagnosticContext: null,
    aiFeedback: '',
    activePrescription: null,
    activeHistoryId: null,
    isGeneratingFeedback: false,
    selectedScenario: null,
    userRole: ROLES.SUPER_ADMIN,
    auditLogs: [],
    contributions: [],
    admins: loadFromStorage(STORAGE_KEYS.ADMINS, [...DEFAULT_ADMINS]),
    currentAdminId: loadFromStorage(STORAGE_KEYS.CURRENT_ADMIN_ID, 'super-admin'),
  },

  getters,

  mutations: {
    SET_IS_LOGGED_IN(state, value) { state.isLoggedIn = value },
    SET_VIEW(state, value) { state.view = value },
    SET_IS_BRIEFING_MODE(state, value) { state.isBriefingMode = value },
    SET_CONTEXT(state, value) {
      state.context = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.CONTEXT, JSON.stringify(value))
      }
    },
    SET_USER_STATS(state, value) {
      state.userStats = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.USER_STATS, JSON.stringify(value))
      }
    },
    SET_HISTORY(state, value) {
      state.history = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(value))
      }
    },
    SET_STUDY_RECORDS(state, value) {
      state.studyRecords = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.STUDY_RECORDS, JSON.stringify(value))
      }
    },
    SET_PRACTICE_RECORDS(state, value) {
      state.practiceRecords = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.PRACTICE_RECORDS, JSON.stringify(value))
      }
    },
    SET_EXPERTS(state, value) {
      state.experts = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.EXPERTS, JSON.stringify(value))
      }
    },
    SET_CASES(state, value) {
      state.cases = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.CASES, JSON.stringify(value))
      }
    },
    SET_TOPICS(state, value) {
      state.topics = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.TOPICS, JSON.stringify(value))
      }
    },
    SET_SCENARIOS(state, value) { state.scenarios = value },
    SET_PRESCRIPTIONS(state, value) { state.prescriptions = value },
    SET_TARGET_TOPIC_ID(state, value) { state.targetTopicId = value },
    SET_PENDING_QUERY(state, value) { state.pendingQuery = value },
    SET_SELECTED_TOPIC(state, value) { state.selectedTopic = value },
    SET_DIAGNOSTIC_CONTEXT(state, value) { state.diagnosticContext = value },
    SET_AI_FEEDBACK(state, value) { state.aiFeedback = value },
    SET_ACTIVE_PRESCRIPTION(state, value) { state.activePrescription = value },
    SET_ACTIVE_HISTORY_ID(state, value) { state.activeHistoryId = value },
    SET_IS_GENERATING_FEEDBACK(state, value) { state.isGeneratingFeedback = value },
    SET_SELECTED_SCENARIO(state, value) { state.selectedScenario = value },
    SET_USER_ROLE(state, value) { state.userRole = value },
    ADD_AUDIT_LOG(state, log) {
      state.auditLogs = [log, ...state.auditLogs].slice(0, 1000)
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.AUDIT_LOGS, JSON.stringify(state.auditLogs))
      }
    },
    SET_CONTRIBUTIONS(state, value) {
      state.contributions = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.CONTRIBUTIONS, JSON.stringify(value))
      }
    },
    SET_ADMINS(state, value) {
      state.admins = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.ADMINS, JSON.stringify(value))
      }
    },
    SET_CURRENT_ADMIN_ID(state, value) {
      state.currentAdminId = value
      if (isPersistenceEnabled) {
        localStorage.setItem(STORAGE_KEYS.CURRENT_ADMIN_ID, value)
      }
    }
  },

  actions: {
    recordStudyAction({ state, commit }, { topic, expert, action, duration = 0 }) {
      const newRecord = {
        id: `study-${Date.now()}`,
        topicId: topic.id,
        topicTitle: topic.title,
        expertName: expert.name,
        expertTitle: expert.title,
        action,
        timestamp: Date.now(),
        duration
      }
      const updated = [newRecord, ...state.studyRecords].slice(0, 100)
      commit('SET_STUDY_RECORDS', updated)
    },

    recordPracticeAction({ state, commit }, { scenario, selectedOption, isCorrect, timeSpent }) {
      const newRecord = {
        id: `practice-${Date.now()}`,
        scenarioId: scenario.id,
        scenarioTitle: scenario.description,
        category: '常规管理',
        selectedOption,
        isCorrect,
        impact: scenario.options.find(o => o.id === selectedOption)?.impact || { morale: 0, efficiency: 0, retention: 0 },
        timestamp: Date.now(),
        timeSpent
      }
      const updated = [newRecord, ...state.practiceRecords].slice(0, 100)
      commit('SET_PRACTICE_RECORDS', updated)
    },

    handleTrackInteraction({ state, commit }, { caseId, type }) {
      const expertCase = state.cases[caseId] || EXPERT_CASES[caseId]
      if (!expertCase) return

      const weights = SCORING_CONFIG.weights

      const updatedExperts = state.experts.map(expert => {
        if (expert.id === expertCase.expertId) {
          const newStats = { ...expert.stats }
          if (type === 'click') newStats.clicks++
          if (type === 'play') newStats.plays++
          if (type === 'bookmark') newStats.bookmarks++
          if (type === 'like') newStats.likes++
          if (type === 'comment') newStats.comments++

          const newPoints =
            (newStats.clicks * weights.click) +
            (newStats.plays * weights.play) +
            (newStats.bookmarks * weights.bookmark) +
            (newStats.likes * weights.like) +
            (newStats.comments * weights.comment)

          return { ...expert, stats: newStats, points: newPoints }
        }
        return expert
      }).sort((a, b) => b.points - a.points)

      commit('SET_EXPERTS', updatedExperts)

      if (type === 'bookmark') {
        const isBookmarked = state.userStats.bookmarks?.includes(caseId)
        const newBookmarks = isBookmarked
          ? state.userStats.bookmarks?.filter(id => id !== caseId)
          : [...(state.userStats.bookmarks || []), caseId]
        commit('SET_USER_STATS', { ...state.userStats, bookmarks: newBookmarks })
      }

      if (type === 'like') {
        const isLiked = state.userStats.likes?.includes(caseId)
        const newLikes = isLiked
          ? state.userStats.likes?.filter(id => id !== caseId)
          : [...(state.userStats.likes || []), caseId]
        commit('SET_USER_STATS', { ...state.userStats, likes: newLikes })
      }
    },

    loadPersistedState({ commit }) {
      if (!isPersistenceEnabled) return
      commit('SET_CONTEXT', loadFromStorage(STORAGE_KEYS.CONTEXT, { ...defaultContext }))
      commit('SET_USER_STATS', loadFromStorage(STORAGE_KEYS.USER_STATS, { ...defaultUserStats }))
      commit('SET_HISTORY', loadFromStorage(STORAGE_KEYS.HISTORY, []))
      commit('SET_STUDY_RECORDS', loadFromStorage(STORAGE_KEYS.STUDY_RECORDS, []))
      commit('SET_PRACTICE_RECORDS', loadFromStorage(STORAGE_KEYS.PRACTICE_RECORDS, []))
      commit('SET_CONTRIBUTIONS', loadFromStorage(STORAGE_KEYS.CONTRIBUTIONS, []))
      commit('SET_TOPICS', loadFromStorage(STORAGE_KEYS.TOPICS, [...TOPICS]))
      commit('SET_CASES', loadFromStorage(STORAGE_KEYS.CASES, { ...EXPERT_CASES }))
      commit('SET_EXPERTS', loadFromStorage(STORAGE_KEYS.EXPERTS, initExperts()))
      commit('SET_ADMINS', loadFromStorage(STORAGE_KEYS.ADMINS, [...DEFAULT_ADMINS]))
      commit('SET_CURRENT_ADMIN_ID', loadFromStorage(STORAGE_KEYS.CURRENT_ADMIN_ID, 'super-admin'))
    }
  },

  plugins: [persistPlugin]
})
